import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type {
  PrivateChatMessageSummary,
  PrivateChatPricing,
} from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import type { createApiClient } from '@fetmode/api-client';
import { useVisitor } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import { formatUsername } from '@fetmode/utils';
import { Button, ShowChatPanel } from '@fetmode/ui';

import { useAuthModal } from '../auth/index.js';
import { useCoinShop } from '../wallet/index.js';

import './PrivateChatTab.css';

type ApiClient = ReturnType<typeof createApiClient>;

const PRIVATE_POLL_MS = 2_500;

const DEFAULT_PRICING: PrivateChatPricing = {
  enabled: true,
  textCoins: 0,
  imageCoins: 50,
  stickerCoins: 5,
};

export interface PrivateChatTabProps {
  username: string;
  api: ApiClient;
  userId: string | null;
  onWalletChange: (total: number) => void;
}

function toPanelMessages(messages: PrivateChatMessageSummary[]) {
  return messages.map((message) => ({
    id: message.id,
    showId: message.modelId,
    userId: message.senderUserId,
    username: message.senderUsername,
    body:
      message.kind === 'IMAGE'
        ? message.body.trim().length > 0
          ? `📷 ${message.body}`
          : '📷 Photo'
        : message.kind === 'STICKER'
          ? message.body
          : message.body,
    kind: 'USER' as const,
    createdAt: message.createdAt,
  }));
}

function PrivateChatVisitorAd({ username }: { username: string }): ReactElement {
  const { openAuthModal } = useAuthModal();
  const { isReturningVisitor } = useVisitor();

  const openJoin = (): void => {
    openAuthModal({
      mode: isReturningVisitor ? 'login' : 'register',
      intent: 'privateChat',
    });
  };

  return (
    <div className="show-chat-shell__private private-chat-ad" role="region" aria-label={t('common.auth.adLabel')}>
      <div className="private-chat-ad__card">
        <div className="private-chat-ad__media" aria-hidden />
        <div className="private-chat-ad__copy">
          <p className="private-chat-ad__eyebrow">{t('common.show.privateChatAdEyebrow')}</p>
          <h3 className="private-chat-ad__title">
            {t('common.show.privateChatAdTitle').replace('{{name}}', formatUsername(username))}
          </h3>
          <p className="private-chat-ad__body">{t('common.show.privateChatAdBody')}</p>
          <ul className="private-chat-ad__list">
            <li>{t('common.show.privateChatAdFeature1')}</li>
            <li>{t('common.show.privateChatAdFeature2')}</li>
            <li>{t('common.show.privateChatAdFeature3')}</li>
          </ul>
          <div className="private-chat-ad__actions">
            <Button
              label={
                isReturningVisitor
                  ? t('common.show.privateChatAdCtaLogin')
                  : t('common.show.privateChatAdCtaRegister')
              }
              onClick={openJoin}
            />
            <button
              type="button"
              className="private-chat-ad__secondary"
              onClick={() => {
                openAuthModal({
                  mode: isReturningVisitor ? 'register' : 'login',
                  intent: 'privateChat',
                });
              }}
            >
              {isReturningVisitor
                ? t('common.show.privateChatAdCtaRegister')
                : t('common.show.privateChatAdCtaLogin')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function PrivateChatTab({
  username,
  api,
  userId,
  onWalletChange,
}: PrivateChatTabProps): ReactElement {
  const { requireAuth } = useAuthModal();
  const { requireSpendCoins } = useCoinShop();
  const [pricing, setPricing] = useState<PrivateChatPricing>(DEFAULT_PRICING);
  const [messages, setMessages] = useState<PrivateChatMessageSummary[]>([]);
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    if (!userId) {
      setIsLoading(false);
      return;
    }
    try {
      const result = await api.getPrivateChat(username);
      setPricing(result.pricing);
      setMessages(result.messages);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.show.privateChatLoadError'));
    } finally {
      setIsLoading(false);
    }
  }, [api, userId, username]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!userId) {
      return;
    }
    const id = window.setInterval(() => {
      void load();
    }, PRIVATE_POLL_MS);
    return () => {
      window.clearInterval(id);
    };
  }, [load, userId]);

  if (!userId) {
    return <PrivateChatVisitorAd username={username} />;
  }

  const send = (kind: 'TEXT' | 'IMAGE' | 'STICKER', body: string): void => {
    requireAuth(() => {
      const needed =
        kind === 'TEXT'
          ? pricing.textCoins
          : kind === 'IMAGE'
            ? pricing.imageCoins
            : pricing.stickerCoins;
      const runSend = (): void => {
        setIsSending(true);
        setError(null);
        void api
          .sendPrivateChatMessage(username, { kind, body })
          .then((result) => {
            setMessages((current) => {
              if (current.some((item) => item.id === result.message.id)) {
                return current;
              }
              return [...current, result.message];
            });
            if (result.walletTotal !== null) {
              onWalletChange(result.walletTotal);
            }
            if (kind === 'TEXT') {
              setText('');
            }
          })
          .catch((err: unknown) => {
            setError(err instanceof ApiError ? err.message : t('common.show.privateChatSendError'));
          })
          .finally(() => {
            setIsSending(false);
          });
      };
      void requireSpendCoins(needed, runSend).then((ok) => {
        if (ok) {
          runSend();
        }
      });
    }, 'privateChat');
  };

  const canSend = pricing.enabled;
  const placeholder = !pricing.enabled
    ? t('common.show.privateChatDisabled')
    : pricing.textCoins > 0
      ? t('common.show.privateChatPlaceholderPaid').replace(
          '{{amount}}',
          String(pricing.textCoins),
        )
      : t('common.show.privateChatPlaceholder');

  const emptyPrompt = isLoading
    ? t('common.show.privateChatLoading')
    : t('common.show.privateChatEmpty');

  return (
    <ShowChatPanel
      layout="room"
      messages={toPanelMessages(messages)}
      messageText={text}
      onMessageTextChange={setText}
      onSend={() => {
        const body = text.trim();
        if (body.length === 0) {
          return;
        }
        send('TEXT', body);
      }}
      isSending={isSending}
      sendError={error}
      canSend={canSend}
      emptyPrompt={emptyPrompt}
      inputPlaceholder={placeholder}
      sendLabel={
        pricing.textCoins > 0
          ? t('common.show.privateChatSendPaid').replace('{{amount}}', String(pricing.textCoins))
          : t('common.show.chatSend')
      }
      sendingLabel={t('common.show.chatSending')}
      composerActions={
        canSend
          ? [
              {
                id: 'photo',
                label:
                  pricing.imageCoins > 0
                    ? t('common.show.privateChatSendPhotoPaid').replace(
                        '{{amount}}',
                        String(pricing.imageCoins),
                      )
                    : t('common.show.privateChatSendPhoto'),
                onClick: () => {
                  send('IMAGE', text.trim() || t('common.show.privateChatPhotoDefault'));
                  setText('');
                },
              },
              {
                id: 'sticker',
                label:
                  pricing.stickerCoins > 0
                    ? t('common.show.privateChatSendStickerPaid').replace(
                        '{{amount}}',
                        String(pricing.stickerCoins),
                      )
                    : t('common.show.privateChatSendSticker'),
                onClick: () => {
                  send('STICKER', '🔥');
                },
              },
            ]
          : undefined
      }
      renderMessage={(message) => (
        <>
          <strong>{formatUsername(message.username)}</strong> <span>{message.body}</span>
        </>
      )}
    />
  );
}
